import React from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { EXERCISE_TARGETS } from '../../../constants';
import { formatDateTimeDisplay } from '../../../utils/base';

const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

const ChartSectionContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '5px',
    flex: '1 0 0',
    alignSelf: 'stretch',
    position: 'relative',
    overflow: 'visible',
}));

const ChartContainer = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'chartSectionHeight'
})(({ theme, chartSectionHeight }) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'stretch',
    height: `${chartSectionHeight}px`,
    width: '100%',
    position: 'relative',
    marginTop: '27px'
}));

const YAxisContainer = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'chartContainerHeight'
})(({ theme, chartContainerHeight }) => ({
    position: 'absolute',
    top: '0',
    left: '0',
    right: '0',
    bottom: '0',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    height: `${chartContainerHeight}px`,
    paddingBottom: '10px', // X축 라벨 공간만 확보
    zIndex: 2,
}));

const YAxisLine = styled(Box)(({ theme }) => ({
    height: '10px',
    width: '100%',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
}));

const YAxisLineInner = styled(Box)(({ theme, color = theme.palette.figma.white[100], dashed = false }) => ({
    position: 'absolute',
    bottom: '0',
    left: '0',
    right: '30px',
    height: '1px',
    backgroundColor: color,
    borderTop: dashed ? `1px dashed ${color}` : 'none',
}));

const YAxisLabel = styled(Typography)(({ theme, color = theme.palette.figma.white[60], fontWeight = 400 }) => ({
    position: 'absolute',
    right: '30px',
    fontSize: '10px',
    color: color,
    fontWeight: fontWeight,
    letterSpacing: '-0.3px',
    whiteSpace: 'nowrap',
    transform: 'translateX(100%) translateY(-50%)',
    top: 'calc(50% + 1px)',
    zIndex: 3,
}));

const BarsContainer = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'chartContainerHeight'
})(({ theme, chartContainerHeight }) => ({
    display: 'flex',
    alignItems: 'end',
    justifyContent: 'space-between',
    height: `${chartContainerHeight}px`,
    flex: 1,
    padding: '0 40px 0 0',
    gap: '0px',
    position: 'relative',
    zIndex: 2,
}));

const Bar = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'isSelected'
})(({ theme, isSelected = false }) => ({
    flex: 1,
    height: '100%',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    padding: '10px 0',
    transition: 'all 0.2s ease',
    gap: '5px',
    zIndex: 5,
}));

const BarFill = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'isMonthly' && prop !== 'height' && prop !== 'color'
})(({ theme, height, color, isMonthly = false }) => ({
    width: isMonthly ? '6px' : '10px',
    height: `${height}px`,
    backgroundColor: color,
    borderRadius: '4px',
    position: 'absolute',
    bottom: '0',
    left: 'calc(50% + 0.429px)',
    transform: 'translateX(-50%)',
    minHeight: '2px',
    transition: 'all 0.2s ease'
}));

const BarLabel = styled(Typography, {
    shouldForwardProp: (prop) => prop !== 'isSelected'
})(({ theme, isSelected = false }) => ({
    position: 'absolute',
    bottom: '-10px',
    fontSize: '12px',
    color: !isSelected ? theme.palette.figma.white[60] : theme.palette.figma.steps,
    fontWeight: !isSelected ? 400 : 500,
    letterSpacing: '-0.36px',
    textAlign: 'center',
    width: '100%',
    minWidth: 'min-content',
}));

const StatisticsChart = ({
    activeView = 'week',
    chartData = [],
    yAxisData = {
        maxValue: 15000,
        minValue: 0,
        averageValue: 0,
        yAxisValues: [0, 6000, 12000]
    },
    selectedBarIndex = -1,
    onBarSelect = null,
    isRangeData = false,
    barColorType = 'target', // target, average
    ...props
}) => {
    const theme = useTheme();
    const { t } = useTranslation();

    const chartSizeGap = (activeView === 'year' && !isRangeData) ? 36 : 0;
    const chartDefaultHeight = isRangeData ? 445 : 353;
    const chartSectionHeight = chartDefaultHeight - chartSizeGap;
    const chartContainerHeight = chartSectionHeight - 20;
    const chartHeight = chartContainerHeight - 20;

    const handleBarClick = (item, index) => {
        // 미래 데이터인지 확인
        const today = new Date();
        const itemDate = new Date(item.day);

        // 미래 날짜인 경우 클릭 방지
        if (itemDate > today) {
            return;
        }

        // 이미 같은 바가 선택되어 있으면 닫기
        if (selectedBarIndex === index) {
            if (onBarSelect) {
                onBarSelect(-1);
            }
            return;
        }

        if (onBarSelect) {
            onBarSelect(index);
        }
    };

    return (
        <ChartSectionContainer {...props}>
            {/* 차트 그리드 영역 */}
            <Box sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                width: '100%',
            }}>
                <ChartContainer chartSectionHeight={chartSectionHeight}>
                    {/* Y축 그리드 */}
                    <YAxisContainer chartContainerHeight={chartContainerHeight}>
                        {/* 심박수 데이터인 경우 모든 그리드 라인 표시 */}
                        {isRangeData ? (
                            yAxisData.yAxisValues.map((value, index) => {
                                const position = (1 - ((value - yAxisData.minValue) / (yAxisData.maxValue - yAxisData.minValue))) * chartHeight;
                                const isBottomLine = index === 0;

                                return (
                                    <Box key={value} sx={{
                                        position: 'absolute',
                                        top: `${position}px`,
                                        left: '270px',
                                        right: '0',
                                        height: '10px',
                                    }}>
                                        <YAxisLabel color={theme.palette.figma.white[60]} fontWeight={400}>
                                            {value.toLocaleString()}
                                        </YAxisLabel>
                                        <Box sx={{
                                            position: 'absolute',
                                            bottom: '0',
                                            left: '-270px',
                                            right: '30px',
                                            height: '1px',
                                            backgroundColor: theme.palette.figma.white[30],
                                            borderTop: !isBottomLine ? `1px dashed ${theme.palette.figma.white[30]}` : 'none',
                                        }} />
                                    </Box>
                                );
                            })
                        ) : (
                            <>
                                {/* 최대값 라인 - 맨 위 */}
                                <YAxisLine>
                                    {yAxisData.maxValue > 0 ?
                                        <>
                                            <YAxisLabel color={theme.palette.figma.white[60]} fontWeight={400}>
                                                {yAxisData.maxValue.toLocaleString()}
                                            </YAxisLabel>
                                            <YAxisLineInner color={theme.palette.figma.white[30]} dashed={'true'} />
                                        </> : <></>}
                                </YAxisLine>

                                {/* 0 라인 - 맨 아래 */}
                                <YAxisLine>
                                    <YAxisLineInner color={theme.palette.figma.white[30]} />
                                </YAxisLine>

                                {/* 목표 라인 - 동적 위치 계산 */}
                                {EXERCISE_TARGETS.STEPS <= yAxisData.maxValue && (
                                    <Box sx={{
                                        position: 'absolute',
                                        top: `${((yAxisData.maxValue - EXERCISE_TARGETS.STEPS) / yAxisData.maxValue) * chartHeight}px`,
                                        left: '270px',
                                        right: '0',
                                        height: '10px',
                                    }}>
                                        <YAxisLabel color={theme.palette.figma.steps} fontWeight={500}>
                                            {EXERCISE_TARGETS.STEPS.toLocaleString()}
                                        </YAxisLabel>
                                        <Box sx={{
                                            position: 'absolute',
                                            bottom: '0',
                                            left: '-270px',
                                            right: '30px',
                                            height: '1px',
                                            backgroundColor: theme.palette.figma.steps,
                                        }} />
                                    </Box>
                                )}

                                {/* 평균 라인 - 동적 위치 계산 */}
                                {yAxisData.averageValue > 0 && (
                                    <Box sx={{
                                        position: 'absolute',
                                        top: `${((yAxisData.maxValue - yAxisData.averageValue) / yAxisData.maxValue) * chartHeight}px`,
                                        left: '270px',
                                        right: '0',
                                        height: '10px',
                                    }}>
                                        <YAxisLabel color={theme.palette.figma.white[60]} fontWeight={400}>{t('average')}</YAxisLabel>
                                        <Box sx={{
                                            position: 'absolute',
                                            bottom: '0',
                                            left: '-270px',
                                            right: '30px',
                                            height: '1px',
                                            backgroundColor: theme.palette.figma.white[30],
                                            borderTop: `1px dashed ${theme.palette.figma.white[30]}`,
                                        }} />
                                    </Box>
                                )}
                            </>
                        )}
                    </YAxisContainer>

                    {/* 바 차트 영역 */}
                    <BarsContainer chartContainerHeight={chartContainerHeight}>
                        {/* 바들 */}
                        {chartData.map((item, index) => {
                            const isSelected = selectedBarIndex >= 0 && selectedBarIndex === index;

                            // 심박수 범위 데이터인 경우
                            if (isRangeData && item.min !== undefined && item.max !== undefined) {
                                const minValue = item.min;
                                const maxValue = item.max;

                                // min과 max가 모두 0이면 바차트를 표시하지 않음
                                if (minValue === 0 && maxValue === 0) {
                                    return (
                                        <Bar
                                            key={index}
                                            isSelected={false}
                                            sx={{
                                                cursor: 'default'
                                            }}
                                        >
                                            <Box sx={{
                                                flex: 1,
                                                width: '100%',
                                                position: 'relative',
                                                display: 'flex',
                                                alignItems: 'flex-end',
                                                justifyContent: 'center',
                                                height: `${chartHeight}px`,
                                            }}>
                                                {/* 빈 공간 - 바차트 없음 */}
                                            </Box>
                                            <BarLabel isSelected={false}>
                                                {(() => {
                                                    if (item.day) {
                                                        if (activeView === 'year') {
                                                            const month = item.day.split('-')[1];
                                                            return parseInt(month);
                                                        } else if (activeView === 'month') {
                                                            const day = item.day.split('-')[2];
                                                            const dayNum = parseInt(day);
                                                            const shouldShow = (dayNum % 5 === 0 || dayNum === 1);
                                                            return shouldShow ? dayNum : '';
                                                        } else {
                                                            const [y, m, d] = item.day.split('-');
                                                            return formatDateTimeDisplay(new Date(y, m - 1, d), 'monthDay');
                                                        }
                                                    } else {
                                                        return item.label || `${index + 22}`;
                                                    }
                                                })()}
                                            </BarLabel>
                                        </Bar>
                                    );
                                }

                                const yAxisMin = yAxisData.minValue;
                                const yAxisMax = yAxisData.maxValue;
                                const yAxisRange = yAxisMax - yAxisMin;

                                // 바의 최대값 위치 계산
                                const maxPosition = (((maxValue - yAxisMin) / yAxisRange)) * chartHeight;
                                // 바의 최소값 위치 계산
                                const minPosition = (((minValue - yAxisMin) / yAxisRange)) * chartHeight;

                                // 바의 실제 높이 (max - min 범위)
                                const actualHeight = Math.max(2, maxPosition - minPosition);
                                // 바의 상단이 max 라인에 닿도록 위치 계산
                                const barTop = minPosition;

                                const barColor = item.color || theme.palette.figma.heartRate;

                                // 미래 데이터인지 확인
                                const today = new Date();
                                const itemDate = new Date(item.day);
                                const isFutureDate = itemDate > today;

                                return (
                                    <Bar
                                        key={index}
                                        isSelected={isSelected}
                                        onClick={() => {
                                            if (!isFutureDate) {
                                                handleBarClick(item, index);
                                            }
                                        }}
                                        sx={{
                                            cursor: isFutureDate ? 'default' : 'pointer'
                                        }}
                                    >
                                        <Box sx={{
                                            flex: 1,
                                            width: '100%',
                                            position: 'relative',
                                            display: 'flex',
                                            alignItems: 'flex-end',
                                            justifyContent: 'center',
                                            height: `${chartHeight}px`,
                                        }}>
                                            {isSelected === true && (
                                                <Box sx={{
                                                    position: 'absolute',
                                                    bottom: `${actualHeight * 2}px`,
                                                    width: '1px',
                                                    height: `${chartContainerHeight - actualHeight * 2}px`,
                                                    backgroundColor: theme.palette.figma.heartRateDark,
                                                }} />
                                            )}

                                            <Box sx={{
                                                width: activeView === 'month' ? '6px' : '10px',
                                                height: `${actualHeight}px`,
                                                backgroundColor: barColor,
                                                borderRadius: '4px',
                                                position: 'absolute',
                                                bottom: `${barTop}px`,
                                                left: 'calc(50% + 0.429px)',
                                                transform: 'translateX(-50%)',
                                                minHeight: '2px',
                                                transition: 'all 0.2s ease',
                                                cursor: 'pointer',
                                            }} />
                                        </Box>

                                        {/* 팝업 - 선택된 바에만 표시 */}
                                        {isSelected === true && (
                                            <Box sx={{
                                                position: 'absolute',
                                                bottom: `${chartContainerHeight}px`,
                                                left: '50%',
                                                transform: 'translateX(-50%)',
                                                backgroundColor: theme.palette.figma.heartRateDark,
                                                borderRadius: '10px',
                                                padding: '5px 10px',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'flex-end',
                                                gap: '2px',
                                                zIndex: 15,
                                                pointerEvents: 'auto',
                                                transition: 'all 0.2s ease',
                                            }}>
                                                {activeView === 'month' && (
                                                    <Typography sx={{
                                                        fontSize: '10px',
                                                        color: theme.palette.figma.white[80],
                                                        fontWeight: 400,
                                                        letterSpacing: '-0.3px',
                                                        lineHeight: 1,
                                                        whiteSpace: 'nowrap',
                                                    }}>
                                                        {(() => {
                                                            const [year, month, day] = item.day.split('-');
                                                            const date = new Date(year, month - 1, day);
                                                            const monthKey = `month_${date.getMonth() + 1}`;
                                                            return `${t(monthKey)} ${parseInt(day)}${t('day')} (${t(DAY_KEYS[date.getDay()])})`;
                                                        })()}
                                                    </Typography>
                                                )}
                                                {activeView === 'year' && (
                                                    <Typography sx={{
                                                        fontSize: '10px',
                                                        color: theme.palette.figma.white[80],
                                                        fontWeight: 400,
                                                        letterSpacing: '-0.3px',
                                                        lineHeight: 1,
                                                        whiteSpace: 'nowrap',
                                                    }}>
                                                        {(() => {
                                                            const [year, month] = item.day.split('-');
                                                            return `${year}${t('year')} ${t('month_' + parseInt(month, 10))}`;
                                                        })()}
                                                    </Typography>
                                                )}
                                                <Typography sx={{
                                                    fontSize: '14px',
                                                    color: theme.palette.figma.heartRate,
                                                    fontWeight: 500,
                                                    letterSpacing: '-0.42px',
                                                    lineHeight: 1,
                                                    whiteSpace: 'nowrap',
                                                }}>
                                                    {minValue} - {maxValue} {t('bpm')}
                                                </Typography>
                                            </Box>
                                        )}
                                        <BarLabel isSelected={isSelected}>
                                            {(() => {
                                                if (item.day && activeView === 'week') {
                                                    const [y, m, d] = item.day.split('-');
                                                    return formatDateTimeDisplay(new Date(y, m - 1, d), 'monthDay');
                                                } else if (item.day) {
                                                    if (activeView === 'year') {
                                                        // 연간차트: 월 번호 표시 (1, 2, 3... 12)
                                                        const month = item.day.split('-')[1];
                                                        return parseInt(month);
                                                    } else if (activeView === 'month') {
                                                        // 월간차트: 5일마다만 표시하되, 월의 마지막 날도 포함
                                                        const day = item.day.split('-')[2];
                                                        const dayNum = parseInt(day);
                                                        const shouldShow = (dayNum % 5 === 0 || dayNum === 1);
                                                        return shouldShow ? dayNum : '';
                                                    } else {
                                                        const [yy, mm, dd] = item.day.split('-');
                                                        return formatDateTimeDisplay(new Date(yy, mm - 1, dd), 'monthDay');
                                                    }
                                                } else {
                                                    // 라벨이 있으면 사용, 없으면 인덱스 기반
                                                    return item.label || `${index + 22}`;
                                                }
                                            })()}
                                        </BarLabel>
                                    </Bar>
                                );
                            }

                            // 기존 디딤수 데이터 처리
                            const value = item.value || 0;

                            // 실제 데이터 값에 맞춰 동적으로 바 높이 계산                              
                            const maxValue = yAxisData.maxValue;
                            const minHeight = 2; // 최소 높이                                

                            const actualHeight = value > 0
                                ? Math.max(minHeight, (value / maxValue) * chartHeight)
                                : minHeight;

                            // 색상은 props에서 전달받은 color 사용, 없으면 기본값
                            const barColor = item.color || (barColorType === 'target' ? (value >= EXERCISE_TARGETS.STEPS ? theme.palette.figma.steps : theme.palette.figma.white[40]) : (value >= yAxisData.averageValue ? theme.palette.figma.steps : theme.palette.figma.white[40]));

                            // 미래 데이터인지 확인
                            const today = new Date();
                            const itemDate = new Date(item.day);
                            const isFutureDate = itemDate > today;

                            return (
                                <Bar
                                    key={index}
                                    isSelected={isSelected}
                                    onClick={() => {
                                        if (!isFutureDate) {
                                            handleBarClick(item, index);
                                        }
                                    }}
                                    sx={{
                                        cursor: isFutureDate ? 'default' : 'pointer'
                                    }}
                                >
                                    <Box sx={{
                                        flex: 1,
                                        width: '100%',
                                        position: 'relative',
                                        display: 'flex',
                                        alignItems: 'flex-end',
                                        justifyContent: 'center',
                                        height: `${chartHeight}px`,
                                    }}>
                                        {isSelected === true && (
                                            <Box sx={{
                                                width: '1px',
                                                height: `${chartContainerHeight}px`,
                                                backgroundColor: theme.palette.figma.stepsDark,
                                            }} />
                                        )}

                                        <BarFill
                                            height={actualHeight}
                                            color={barColor}
                                            isMonthly={activeView === 'month'}
                                        />
                                    </Box>

                                    {/* 팝업 - 선택된 바에만 표시 */}
                                    {isSelected === true && (() => {
                                        // 월간/연간 탭에서 좌측 바 클릭 시 말풍선이 잘리지 않도록 위치 조정
                                        const isMonthOrYear = activeView === 'month' || activeView === 'year';
                                        const totalBars = chartData.length;
                                        const isLeftBar = index < totalBars * 0.3; // 좌측 30% 영역
                                        const isRightBar = index > totalBars * 0.7; // 우측 30% 영역

                                        // 좌측 바일 경우 말풍선을 오른쪽으로 이동, 우측 바일 경우 왼쪽으로 이동
                                        const tooltipLeft = isMonthOrYear && isLeftBar
                                            ? '0%'
                                            : isMonthOrYear && isRightBar
                                                ? 'auto'
                                                : '50%';
                                        const tooltipRight = isMonthOrYear && isRightBar ? '0%' : 'auto';
                                        const tooltipTransform = isMonthOrYear && isLeftBar
                                            ? 'translateX(0)'
                                            : isMonthOrYear && isRightBar
                                                ? 'translateX(0)'
                                                : 'translateX(-50%)';

                                        return (
                                            <Box sx={{
                                                position: 'absolute',
                                                bottom: `${chartContainerHeight}px`,
                                                left: tooltipLeft,
                                                right: tooltipRight,
                                                transform: tooltipTransform,
                                                backgroundColor: theme.palette.figma.stepsDark,
                                                borderRadius: '10px',
                                                padding: '5px 10px',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'flex-end',
                                                gap: '2px',
                                                zIndex: 15,
                                                pointerEvents: 'auto',
                                                transition: 'all 0.2s ease',
                                            }}>
                                                {activeView === 'month' && (
                                                    <Typography sx={{
                                                        fontSize: '10px',
                                                        color: theme.palette.figma.white[80],
                                                        fontWeight: 500,
                                                        letterSpacing: '-0.3px',
                                                        lineHeight: 1,
                                                        whiteSpace: 'nowrap',
                                                    }}>
                                                        {(() => {
                                                            const [year, month, day] = item.day.split('-');
                                                            const date = new Date(year, month - 1, day);
                                                            const monthKey = `month_${date.getMonth() + 1}`;
                                                            return `${t(monthKey)} ${parseInt(day)}${t('day')} (${t(DAY_KEYS[date.getDay()])})`;
                                                        })()}
                                                    </Typography>
                                                )}
                                                {activeView === 'year' && (
                                                    <Typography sx={{
                                                        fontSize: '10px',
                                                        color: theme.palette.figma.white[80],
                                                        fontWeight: 500,
                                                        letterSpacing: '-0.3px',
                                                        lineHeight: 1,
                                                        whiteSpace: 'nowrap',
                                                    }}>
                                                        {(() => {
                                                            const [year, month] = item.day.split('-');
                                                            return `${year}${t('year')} ${t('month_' + parseInt(month, 10))}`;
                                                        })()}
                                                    </Typography>
                                                )}
                                                {activeView === 'year' && (
                                                    <Typography sx={{
                                                        fontSize: '14px',
                                                        color: theme.palette.figma.steps,
                                                        fontWeight: 500,
                                                        letterSpacing: '-0.42px',
                                                        lineHeight: 1,
                                                        whiteSpace: 'nowrap',
                                                    }}>
                                                        {t('average')} {item?.value?.toLocaleString()}
                                                    </Typography>
                                                )}
                                                {activeView === 'year' && (
                                                    <Typography sx={{
                                                        fontSize: '14px',
                                                        color: theme.palette.figma.steps,
                                                        fontWeight: 500,
                                                        letterSpacing: '-0.42px',
                                                        lineHeight: 1,
                                                        whiteSpace: 'nowrap',
                                                    }}>
                                                        {t('all')} {item?.stepsTotal?.toLocaleString()}
                                                    </Typography>
                                                )}
                                                {activeView === 'year' && (
                                                    <Typography sx={{
                                                        fontSize: '10px',
                                                        color: theme.palette.figma.white[80],
                                                        fontWeight: 400,
                                                        letterSpacing: '-0.3px',
                                                        lineHeight: 1,
                                                        whiteSpace: 'nowrap',
                                                    }}>
                                                        <span>{t('goal_achieved')} </span>
                                                        <span style={{ color: theme.palette.figma.steps }}>
                                                            {item?.goalDays}
                                                        </span>
                                                    </Typography>
                                                )}
                                                {activeView !== 'year' && (
                                                    <>
                                                        <Typography sx={{
                                                            fontSize: '14px',
                                                            color: theme.palette.figma.steps,
                                                            fontWeight: 500,
                                                            letterSpacing: '-0.42px',
                                                            lineHeight: 1,
                                                            whiteSpace: 'nowrap',
                                                        }}>
                                                            {item.value.toLocaleString()}{t('didim')}
                                                        </Typography>
                                                        <Typography sx={{
                                                            fontSize: '10px',
                                                            color: theme.palette.figma.white[80],
                                                            fontWeight: 400,
                                                            letterSpacing: '-0.3px',
                                                            lineHeight: 1,
                                                        }}>
                                                            {item.floors || 0}{t('floors')}
                                                        </Typography>
                                                    </>
                                                )}
                                            </Box>
                                        );
                                    })()}
                                    <BarLabel isSelected={isSelected}>
                                        {(() => {
                                            if (item.day && activeView === 'week') {
                                                const [y, m, d] = item.day.split('-');
                                                return formatDateTimeDisplay(new Date(y, m - 1, d), 'monthDay');
                                            } else if (item.day) {
                                                if (activeView === 'year') {
                                                    // 연간차트: 월 번호 표시 (1, 2, 3... 12)
                                                    const month = item.day.split('-')[1];
                                                    return parseInt(month);
                                                } else if (activeView === 'month') {
                                                    // 월간차트: 5일마다만 표시하되, 월의 마지막 날도 포함
                                                    const day = item.day.split('-')[2];
                                                    const dayNum = parseInt(day);
                                                    const shouldShow = (dayNum % 5 === 0 || dayNum === 1);
                                                    return shouldShow ? dayNum : '';
                                                } else {
                                                    const [yy, mm, dd] = item.day.split('-');
                                                    return formatDateTimeDisplay(new Date(yy, mm - 1, dd), 'monthDay');
                                                }
                                            } else {
                                                // 라벨이 있으면 사용, 없으면 인덱스 기반
                                                return item.label || `${index + 22}`;
                                            }
                                        })()}
                                    </BarLabel>
                                </Bar>
                            );
                        })}
                    </BarsContainer>
                </ChartContainer>
            </Box>
        </ChartSectionContainer>
    );
};

export default StatisticsChart;
